import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";
import { refreshedOutline } from "component/shared/outlined-button/style";

export const styles = (theme: Theme) => {
	return ({
		// Grows into any free height of a flex-column host so a short page still anchors the bar to the
		// bottom of the viewport (MAX-4355). Its min height stands in for the bar's old 8px top margin. A
		// spacer rather than the bar's own marginTop: "auto", so the bar keeps its sticky parent.
		spacer: {
			flexGrow: 1,
			minHeight: 8,
		},
		root: {
			position: "sticky",
			bottom: 0,
			backgroundColor: theme.palette.background.paper,
			borderTop: `1px solid ${theme.palette.divider}`,
			padding: "12px 24px",
			display: "flex",
			justifyContent: "flex-end",
			gap: 12,
			zIndex: 100,
			marginLeft: -32,
			marginRight: -32,
		},
		// Must come after root: its margins and padding override root's, and with the two applied as
		// separate classes of equal specificity, the later rule in this sheet wins.
		// Spans the viewport from a width-capped page while its buttons stay aligned to the content
		// (MAX-4139). The bar keeps the content's width and paints its background and top border out to
		// the window edges with a border-image outset, which is ink overflow and adds no scroll width. A
		// 100vw bar counted the vertical scrollbar and scrolled the page sideways (MAX-4507).
		fullBleed: {
			marginLeft: 0,
			marginRight: 0,
			border: 0,
			borderImage: `linear-gradient(${theme.palette.divider} 1px, ${theme.palette.background.paper} 1px) fill 0 / 0 / 0 100vmax`,
		},
		// refreshed: Figma's Button Size=Large (20px label, 24px line, 12×16 padding → 48px tall). Set here
		// because the theme's MuiButton root override replaces the size styles `size="large"` would bring.
		large: {
			fontSize: 20,
			lineHeight: "24px",
			padding: theme.spacing(1.5, 2),
		},
		// Large Secondary has the 2px outline; trim the padding so Cancel stays as tall as Save Changes.
		outlined: {
			...refreshedOutline,
			padding: `calc(${theme.spacing(1.5)} - 2px) calc(${theme.spacing(2)} - 2px)`,
		},
	} satisfies Record<string, CSSObject>);
};
